// 공통 알림 다이얼로그 — 안내 문구 + 확인 버튼 1개.
// - useAlertDialog() 훅으로 어디서든 호출.
// - App 루트에 <AlertDialogHost /> 하나만 두면 됨.
import { create } from 'zustand';
import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './AlertDialog.module.scss';

interface AlertDialogState {
  open: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  show: (options: {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
  }) => void;
  close: () => void;
}

export const useAlertDialog = create<AlertDialogState>((set) => ({
  open: false,
  title: '안내',
  message: '',
  confirmText: '확인',
  cancelText: undefined,
  onConfirm: undefined,
  onCancel: undefined,
  show: ({ title = '안내', message, confirmText = '확인', cancelText, onConfirm, onCancel }) =>
    set({ open: true, title, message, confirmText, cancelText, onConfirm, onCancel }),
  close: () => set({ open: false, onConfirm: undefined, onCancel: undefined, cancelText: undefined }),
}));

export function AlertDialogHost() {
  const { open, title, message, confirmText, cancelText, onConfirm, onCancel, close } = useAlertDialog();

  const handleConfirm = () => {
    close();
    if (onConfirm) {
      const fn = onConfirm;
      queueMicrotask(fn);
    }
  };

  const handleCancel = () => {
    close();
    if (onCancel) {
      const fn = onCancel;
      queueMicrotask(fn);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="alert-backdrop"
          className={styles.backdrop}
          onClick={handleCancel}
          role="dialog"
          aria-modal="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
        >
          <motion.div
            className={styles.dialog}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.985 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <div className={styles.head}>
              <span className={styles.title}>{title}</span>
              <button
                type="button"
                className={styles.close}
                onClick={handleCancel}
                aria-label="닫기"
              >
                <X size={16} strokeWidth={2} />
              </button>
            </div>
            <div className={styles.body}>{message}</div>
            <div className={styles.foot}>
              {cancelText && (
                <button id="btn-alert-dialog-cancel"
                  type="button"
                  className={styles.cancel}
                  onClick={handleCancel}
                >
                  {cancelText}
                </button>
              )}
              <button id="btn-alert-dialog-confirm"
                type="button"
                className={styles.confirm}
                onClick={handleConfirm}
                autoFocus
              >
                {confirmText}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
